import React, {
    FormEvent,
    ReactElement, useEffect, useState,
} from "react";
import { Form } from "react-bootstrap";
import Editor from "../../../../components/editor/Editor";
import TimedMessage from "../../../../components/timed-message/TimedMessage";

import Select from "../../../../components/select/Select";

import { Loading } from "../../../../components";
import BooleanSelect from "../../../../components/pickers/boolean/BooleanSelect";
import usePortal from "../../../../contexts/PortalContext";
import {
    DeflectionTicketStatusAssignment,
    GetCurrentPortalDocument,
    GetSettingsDocument,
    useGetArtificialIntelligenceSettingsQuery,
    useSaveArtificialIntelligenceSettingsMutation,
    useSaveSettingsMutation,
} from "../../../../__generated__/api";
import { SettingsProps } from "../base";

const DEFLECTION_ASSIGNMENT_STATUSES = {
    [DeflectionTicketStatusAssignment.ChatRequested]: "Show tickets to agents while deflection is waiting",
    [DeflectionTicketStatusAssignment.DeflectionWaiting]:
        "Do not show tickets to agents while deflection is waiting",
};

const FEATURE_FLAG_AI_ENABLED = "frontend.ai.enabled";

export default function ArtificialIntelligence(props: SettingsProps): ReactElement {
    const [save] = useSaveSettingsMutation();
    const [saveSettings] = useSaveArtificialIntelligenceSettingsMutation();

    const [message, setMessage] = useState<string>();
    const [deflectionTicketStatusAssignment, setDeflectionTicketStatusAssignment] = useState<DeflectionTicketStatusAssignment>();

    const [isEnabled, setIsEnabled] = useState<boolean>(false);

    const { data, loading: isLoading } = useGetArtificialIntelligenceSettingsQuery();
    const { featureFlags } = usePortal();

    const deflection = props.settings?.artificialIntelligence?.deflection;
    useEffect(() => {
        if (!props) {
            return;
        }

        setMessage(deflection?.message?.rawText as string);
        setDeflectionTicketStatusAssignment(deflection?.deflectionTicketStatusAssignment
            ?? DeflectionTicketStatusAssignment.ChatRequested);
    }, [
        deflection?.message?.rawText,
    ]);

    useEffect(() => {
        if (!data?.artificialIntelligenceSettings) {
            return;
        }
        setIsEnabled(data?.artificialIntelligenceSettings.isEnabled ?? false);
    }, [data?.artificialIntelligenceSettings?.isEnabled]);

    const [isSaved, setIsSaved] = useState(false);

    const onSubmit = (event: FormEvent) => {
        event.preventDefault();
        (async () => {
            await save({
                refetchQueries: [
                    { query: GetSettingsDocument },
                ],
                variables: {
                    artificialIntelligence: {
                        deflection: {
                            message: message ? {
                                rawText: message,
                            } : undefined,
                            deflectionTicketStatusAssignment: deflectionTicketStatusAssignment
                                ?? DeflectionTicketStatusAssignment.ChatRequested,
                        },

                    },
                },
            });
            if (featureFlags.has(FEATURE_FLAG_AI_ENABLED)) {
                await saveSettings({
                    refetchQueries: [
                        { query: GetCurrentPortalDocument },
                    ],
                    variables: {
                        settings: {
                            isEnabled,
                        },
                    },
                });
            }

            setIsSaved(true);
        })();
    };

    return (

        <form className="flex-grow-1" onSubmit={onSubmit}>
            <TimedMessage shouldShow={isSaved} title="Saved" onClose={() => setIsSaved(false)}>
                Settings saved.
            </TimedMessage>

            {isLoading ? <Loading /> : (
                <div>
                    <div className="border p-3 rounded mb-4 bg-white">

                        <h5 className="mb-3 text-light">
                            Deflection
                        </h5>

                        <Form.Group className="mb-4">
                            <Form.Label className="fw-bolder">
                                Deflection Message
                            </Form.Label>

                            <div className="bg-white border-light border rounded">
                                <Editor
                                    text={message as string}
                                    onChange={msg => setMessage(msg)}
                                    isScrollDisabled
                                    shouldFocus={false}
                                    placeholder="Here are some articles that might help"
                                />
                            </div>
                            <Form.Text>
                                A custom message to show when deflection occurs. If left empty the default message will display
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Label className="fw-bolder">
                                Deflection Ticket Availability
                            </Form.Label>

                            <Select
                                className="text-gray"
                                placeholder=""
                                onChange={
                                    value => setDeflectionTicketStatusAssignment(value.value as DeflectionTicketStatusAssignment)
                                }
                                options={
                                    Object.entries(DEFLECTION_ASSIGNMENT_STATUSES).map(([value, label]) => ({ value, label }))
                                }
                                value={!deflectionTicketStatusAssignment ? undefined : {
                                    value: deflectionTicketStatusAssignment,
                                    label: DEFLECTION_ASSIGNMENT_STATUSES[deflectionTicketStatusAssignment],
                                }}
                            />
                            <Form.Text>
                                Should a ticket that is waiting for a deflection response be available to an agent?
                            </Form.Text>
                        </Form.Group>

                    </div>

                    {featureFlags.has(FEATURE_FLAG_AI_ENABLED) && (
                        <div className="border p-3 rounded mb-4 bg-white">

                            <h5 className="mb-0 text-light">
                                AI Assist
                            </h5>
                            <div className="text-light">
                                AI assist helps agents use AI Features such
                                as conversation summarization, rewording, etc.
                            </div>

                            <Form.Group className="mt-4">
                                <Form.Label className="fw-bolder">
                                    Enabled
                                </Form.Label>

                                <BooleanSelect
                                    className="text-gray"
                                    placeholder=""
                                    value={isEnabled}
                                    onChange={value => setIsEnabled(value ?? false)}
                                />
                                <Form.Text>
                                    Should AI features be enabled. Keep in mind, AI features utilize
                                    OpenAI technology and, if enabled, will automatically send conversations,
                                    user names,
                                    and other data to OpenAI. By enabling this feature you agree the OpenAI
                                    terms of service.
                                </Form.Text>
                            </Form.Group>

                        </div>
                    )}
                </div>
            )}

            <div className="d-flex justify-content-end mt-3 fixed-bottom border-top p-3 bg-white">
                <button type="submit" className="btn btn-primary">
                    Save
                </button>
            </div>

        </form>
    );
}
